import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { ScrollReveal } from '@/components/base/ScrollReveal';
import { homeFAQ } from '@/mocks/faq';

const faqPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: homeFAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    document.title = '清文華泉常見問題｜書籍、購書通路與品牌合作';
  }, []);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <JsonLd data={faqPageSchema} />
      <Navbar />
      <main className="bg-gradient-to-b from-background-50 via-primary-50/30 to-secondary-50/40">

        {/* Intro Section */}
        <ScrollReveal direction="up">
          <section className="w-full pt-28 pb-10 md:pt-36 md:pb-12">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-6">
                常見問題
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">
                清文華泉常見問題
              </h1>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                清文華泉 Cinva 以 AIGC 彩色書籍、科普與歷史類書籍為主要出版方向。本品牌委託崧博出版經銷，官方網站由崧博出版製作。完整書名、作者、ISBN與出版日期可由崧博出版書籍資料庫查詢；價格、庫存、版本與購買方式則以各合作通路頁面為準。清文華泉不提供電子書、POD或有聲書等出版製作代工服務。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                本頁整理清文華泉品牌、正式書籍、書目查詢、合作購書通路及品牌合作等常見問題。完整出版資訊請以崧博出版書籍資料庫與各通路正式頁面為準。
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  認識清文華泉
                  <i className="ri-arrow-right-line"></i>
                </Link>
                <a
                  href="https://catalog.sonbook.net/"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-foreground-300/60 text-foreground-700 rounded-full text-sm font-medium hover:bg-foreground-950/5 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  前往崧博出版書籍資料庫
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* FAQ Items */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-8">
                常見問答
              </span>
              <div className="space-y-3">
                {homeFAQ.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-100 transition-colors duration-300"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-2">
                        {item.question}
                      </h3>
                      <div className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full transition-colors duration-300 ${
                        openIndex === index ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className={`text-sm transition-transform duration-300 ${openIndex === index ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                      </div>
                    </button>
                    <div
                      className={`transition-all duration-300 overflow-hidden ${
                        openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                    >
                      <div className="px-5 pb-5 text-sm md:text-base text-foreground-700 leading-relaxed">
                        {item.answer}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Book Catalog CTA */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto text-center">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-5">
                書目查詢
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                查詢正式書目資訊
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                如需確認完整書名、作者、ISBN、出版日期及其他正式出版資料，請前往崧博出版書籍資料庫查詢。
              </p>
              <a
                href="https://catalog.sonbook.net/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                前往崧博出版書籍資料庫
                <i className="ri-external-link-line"></i>
              </a>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Store Partners CTA */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto text-center">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-200 rounded-full mb-5">
                購書通路
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                查看清文華泉合作購書通路
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6">
                不同書籍的上架平台可能不同，請由合作通路頁選擇適合的平台，並以各通路即時商品資訊為準。
              </p>
              <Link
                to="/stores"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                查看合作購書通路
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>
        </ScrollReveal>

        <div className="w-full px-6 md:px-10 max-w-3xl mx-auto"><div className="border-t border-primary-200/30"></div></div>

        {/* Brand Cooperation */}
        <ScrollReveal direction="up">
          <section className="w-full py-14 md:py-16 pb-20 md:pb-28">
            <div className="w-full px-6 md:px-10 max-w-3xl mx-auto">
              <span className="inline-block text-xs tracking-[0.15em] text-primary-600 px-3 py-1 border border-primary-300 rounded-full mb-4">
                合作洽詢
              </span>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
                品牌經銷與合作洽詢
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                如有清文華泉品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過官方聯絡方式提出詢問。實際合作內容與條件將依個別情況確認。
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
            </div>
          </section>
        </ScrollReveal>

      </main>
      <Footer />
    </>
  );
}